
import { equalWithEps } from '@/utils/math';
import { isDesktop } from '~/utils/screenSizes';

/**
 * A container that displays all of its elements linearly in a row, cutting
 * horizontal overflow, with navigation buttons to scroll, as well as a
 * scrollbar.
 *
 * @todo This component should, perhaps, have gradients on the sides where
 *       content is hidden.
 * @todo We should perhaps indicate that the left-right navigation buttons are
 *       at their bounds.
 */
export default {
  name: 'OverflowContainer',
  props: {
    /**
     * An optional icon added before the title
     */
    icon: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * An optional title added to the top of the carousel.
     */
    title: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * The scroll gait in pixels.
     */
    stepSize: {
      required: false,
      type: Number,
      default: 128,
    },
    /**
     * Enable navigation buttons.
     */
    withNavButtons: {
      required: false,
      type: Boolean,
      default: true,
    },
    /**
     * The scroll animation speed in pixels per frame. Set to 0 to disable
     * animations.
     */
    animationSpeed: {
      required: false,
      type: Number,
      default: 16,
    },
    itemGap: {
      required: false,
      type: String,
      default: '3',
    },
  },
  data() {
    return {
      animation: { interval: null, target: 0 },
    };
  },
  computed: {
    isDesktopSize() {
      return isDesktop();
    },
    itemGapClass() {
      return `gap-${this.itemGap}`;
    },
  },
  methods: {
    scroll(amount) {
      const scrollEpsilon = this.stepSize % this.animationSpeed;
      const direction = amount / Math.abs(amount);
      const containerDOM = this.$refs.carouselContainerContent;

      // In non-animated mode, we just need to set our current position to the
      // target animation.
      if (this.animationSpeed === 0) {
        containerDOM.scrollLeft += amount;
        return;
      }

      // Otherwise, we need to, first, increase our target animation.
      this.animation.target += amount;

      // Now, we need to set up the animation interval. If it already exists,
      // we don't need to do anything else.
      if (this.animation.interval !== null) {
        return;
      }

      // Otherwise, we need to hook it in.
      this.animation.interval = setInterval(() => {
        const previousPosition = containerDOM.scrollLeft;
        containerDOM.scrollLeft += this.animationSpeed * direction;

        // We do need to check tolerance-based here since we are doing a linear
        // animation. Because of this, the animation might overshoot or
        // undershoot. This error happens if we divide:
        // (n * stepSize) / animationSpeed with a remainder.
        // Also need to check if we didn't move, meaning we've scrolled to the boundaries
        // In this case we reajust the target
        if (
          equalWithEps(
            containerDOM.scrollLeft,
            this.animation.target,
            scrollEpsilon
          ) ||
          previousPosition === containerDOM.scrollLeft
        ) {
          clearInterval(this.animation.interval);
          this.animation.interval = null;
          this.animation.target = containerDOM.scrollLeft;
        }
      }, 16);
    },
  },
};
